/*
 * ET - Announcement Bar (sections/et-announcement-bar.liquid).
 *
 * Two custom elements, so everything re-initialises by itself on shopify:section:load and tears itself down on
 * shopify:section:unload — a section reload replaces the DOM, which fires disconnectedCallback and then
 * connectedCallback. No instance owns a timer and no instance adds a document-level listener.
 *
 *   <et-announcement-bar>       the message row. Follows Impact's carousel and paints the bar in the active
 *                               message's own background colour, and hides the arrows on messages that ask for it
 *                               (the Figma sale variant exports both chevrons at opacity 0) while keeping their
 *                               16px slots so nothing shifts.
 *   <et-announcement-countdown> the sale message's live countdown. Every instance shares ONE 1s ticker, so ten
 *                               marquee copies still cost one timer, and the timer is cleared the moment the last
 *                               instance disconnects. With data-et-daily ("Midnight, every day") it counts to the
 *                               visitor's next local midnight and starts again from 24 hours instead of ending.
 *
 * Digits never change a pill's width: the value span is a fixed two-character inline-block with tabular figures
 * (assets/et-announcement-bar.css), so the bar height and the message width stay put while the clock runs.
 */
(function () {
  'use strict';

  if (!window.customElements || !window.HTMLElement) return;

  var MESSAGE_SELECTOR = '.et-announcement-bar__message';
  var BAR_SELECTOR = '.et-announcement-bar__bar';

  /* ------------------------------------------------------------------ *
   * One ticker for every countdown on the page
   * ------------------------------------------------------------------ */

  var live = new Set();
  var timerId = null;

  function tick() {
    live.forEach(function (instance) {
      instance.refresh();
    });
  }

  function startTicker() {
    if (timerId === null && live.size > 0) {
      timerId = window.setInterval(tick, 1000);
    }
  }

  function stopTicker() {
    if (timerId !== null && live.size === 0) {
      window.clearInterval(timerId);
      timerId = null;
    }
  }

  // A backgrounded tab throttles setInterval, so re-read the clock the moment the tab comes back.
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) tick();
  });

  function pad(value) {
    return value < 10 ? '0' + value : String(value);
  }

  function nextMidnight() {
    var date = new Date();
    date.setHours(24, 0, 0, 0);
    return date.getTime();
  }

  /* ------------------------------------------------------------------ *
   * The row: the message on screen drives the bar colour and the arrows
   * ------------------------------------------------------------------ */

  class EtAnnouncementBar extends HTMLElement {
    connectedCallback() {
      this._onSelect = this._onSelect.bind(this);
      // carousel:select bubbles from Impact's <announcement-bar> on connect (index 0) and on every change, just
      // before the 0.2s slide fade; the CSS transition on the bar keeps the colour in step with it.
      this.addEventListener('carousel:select', this._onSelect);
      this.addEventListener('carousel:change', this._onSelect);
    }

    disconnectedCallback() {
      this.removeEventListener('carousel:select', this._onSelect);
      this.removeEventListener('carousel:change', this._onSelect);
    }

    _onSelect(event) {
      var slide = event.detail && event.detail.slide;
      if (slide && slide.dataset) this.apply(slide);
    }

    apply(slide) {
      this.dataset.etArrows = slide.dataset.etArrows === 'hidden' ? 'hidden' : 'visible';

      var bar = this.closest(BAR_SELECTOR);
      if (!bar) return;

      var background = slide.dataset.etBackground;
      if (background) {
        bar.style.setProperty('--et-announcement-bar-background', background);
      } else {
        bar.style.removeProperty('--et-announcement-bar-background');
      }

      // The arrow buttons are siblings of the carousel, so a message's own arrow colour has to be set on the row.
      var arrow = slide.dataset.etArrowColor;
      if (arrow) {
        this.style.setProperty('--et-announcement-bar-arrow', arrow);
      } else {
        this.style.removeProperty('--et-announcement-bar-arrow');
      }
    }

    /* Repaint for whichever message is on screen — used after one removes itself. */
    resync() {
      var carousel = this.querySelector('announcement-bar');
      var slide = carousel && carousel.selectedSlide;
      if (slide) this.apply(slide);
    }
  }

  if (!window.customElements.get('et-announcement-bar')) {
    window.customElements.define('et-announcement-bar', EtAnnouncementBar);
  }

  /* ------------------------------------------------------------------ *
   * The countdown
   * ------------------------------------------------------------------ */

  class EtAnnouncementCountdown extends HTMLElement {
    connectedCallback() {
      this._daily = this.hasAttribute('data-et-daily');
      this._endAt = this._daily ? nextMidnight() : (parseInt(this.dataset.etEnd, 10) || 0) * 1000;
      this._onEnd = this.dataset.etOnEnd || 'hide';
      this._values = Array.prototype.slice.call(this.querySelectorAll('[data-et-unit]'));
      this._units = this.querySelector('.et-announcement-bar__units');
      this._endLabel = this.querySelector('.et-announcement-bar__countdown-end');
      this._done = false;

      if (!this._endAt || this._values.length === 0) return;

      this.refresh();
      if (!this._done) {
        live.add(this);
        startTicker();
      }
    }

    disconnectedCallback() {
      live.delete(this);
      stopTicker();
    }

    refresh() {
      if (this._daily && Date.now() >= this._endAt) this._endAt = nextMidnight();

      var remaining = Math.floor((this._endAt - Date.now()) / 1000);

      if (remaining <= 0) {
        this._values.forEach(function (node) {
          node.textContent = '00';
        });
        // A daily countdown shows its last second as zeros and starts again from 24 hours on the next tick.
        if (!this._daily) this._finish();
        return;
      }

      var totals = {
        days: Math.floor(remaining / 86400),
        hours: Math.floor(remaining / 3600),
        minutes: Math.floor(remaining / 60),
        seconds: remaining
      };
      var parts = {
        days: totals.days,
        hours: totals.hours % 24,
        minutes: totals.minutes % 60,
        seconds: remaining % 60
      };

      this._values.forEach(function (node) {
        var unit = node.dataset.etUnit;
        var source = node.dataset.etMode === 'total' ? totals : parts;
        if (typeof source[unit] !== 'number') return;
        var next = pad(source[unit]);
        if (node.textContent !== next) node.textContent = next;
      });
    }

    _finish() {
      if (this._done) return;
      this._done = true;
      live.delete(this);
      stopTicker();

      if (this._onEnd === 'zeros') return;

      if (this._onEnd === 'message') {
        if (this._endLabel) {
          if (this._units) this._units.hidden = true;
          this._endLabel.hidden = false;
        }
        return;
      }

      this._retire();
    }

    /* "Hide message": drop the whole message and let the carousel carry on without it. */
    _retire() {
      var message = this.closest(MESSAGE_SELECTOR);
      if (!message) {
        this.hidden = true;
        return;
      }

      var row = message.closest('et-announcement-bar');
      var carousel = message.parentElement;
      var isCarousel = carousel && typeof carousel.visibleItems !== 'undefined';
      var wasSelected = isCarousel && carousel.selectedSlide === message;

      message.hidden = true;

      // In auto-scrolling mode the parent is a marquee copy, not the carousel: hiding the message is all there is.
      if (!isCarousel) return;

      if (carousel.visibleItems.length === 0) {
        var section = message.closest('.shopify-section');
        if (section) section.hidden = true;
        document.documentElement.style.setProperty('--announcement-bar-height', '0px');
        return;
      }

      if (wasSelected) {
        // control:next is the event Impact's own next-button dispatches, so the carousel keeps its own bookkeeping.
        carousel.dispatchEvent(new CustomEvent('control:next'));
      }

      if (row && typeof row.resync === 'function') row.resync();
    }
  }

  if (!window.customElements.get('et-announcement-countdown')) {
    window.customElements.define('et-announcement-countdown', EtAnnouncementCountdown);
  }

  /* ------------------------------------------------------------------ *
   * Theme editor: a section reload re-runs connectedCallback on its own. This only makes sure the bar is repainted
   * for whichever message ends up on screen after the reload.
   * ------------------------------------------------------------------ */

  document.addEventListener('shopify:section:load', function (event) {
    var row = event.target.querySelector && event.target.querySelector('et-announcement-bar');
    if (row && typeof row.resync === 'function') row.resync();
  });
})();
